import {reportingPost} from 'api';
import {sendSentryException} from 'utils/error-handling';
import ObjectUtils from '@spothero/utils/object';
import {asDecimalCurrency, asInteger} from 'utils/number-format';

/**
 * Formats the request data for the dashboard yoy summary api endpoints
 * @function
 * @param {object} data - The data to format
 * @return {AccountingDashboardReportSummary[]}
 *
 * @memberof module:api/accounting/dto
 *
 */
export const formatGetYoySummary = (data) => {
    const formattedData = ObjectUtils.camelizeKeys(data);

    const formattedRevenue = {
        ...formattedData.revenue,
        value: asDecimalCurrency(formattedData.revenue.value / 100),
        previousValue: asDecimalCurrency(
            formattedData.revenue.previousValue / 100
        ),
    };

    const formattedReservations = {
        ...formattedData.reservations,
        value: asInteger(formattedData.reservations.value),
        previousValue: asInteger(formattedData.reservations.previousValue),
    };

    return {
        revenue: formattedRevenue,
        reservations: formattedReservations,
    };
};
/**
 * Formats the request data for the dashboard yoy summary api endpoints
 * @function
 * @param {object} data - The data to format
 * @return {YoyRequest}
 *
 * @memberof module:api/accounting/dto
 */
export const formatRequestData = (data) => {
    return {
        year: parseInt(data.currentYear, 10),
        facilityIDs: data.facilityIds,
        // eslint-disable-next-line camelcase
        current_date: data.currentDate,
    };
};
/**
 * Gets the dashboard yoy accounting data for a collection of facilities
 * @function
 * @param {YoyRequest} data - The parameters for the report
 * @param {Object} config - The axios config
 * @return {Promise<AccountingDashboardReportSummary>}
 * @throws {Error}
 *
 * @see '/control-panel-service/v1/yoy/tiles
 */
export const getYoySummary = async (data, config = {}) => {
    try {
        const response = await reportingPost(
            'yoy/tiles',
            formatRequestData(data),
            config
        );

        return formatGetYoySummary(response.data.data);
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Formats the request data for the dashboard yoy graphs api endpoints
 * @function
 * @param {object} data - The data to format
 * @return {AccountingDashboardReportSummary[]}
 *
 * @memberof module:api/accounting/dto
 */
export const formatGetYoyGraphs = (data) => {
    const formattedYoyData = ObjectUtils.camelizeKeys(data);

    const formattedData = (innerData, isRevenue) => {
        return innerData.map((item) => {
            const id = item.month;
            const values = Object.entries(item) // Convert object to array of key-value pairs
                .filter(([key]) => key !== 'month') // Exclude the 'month' key
                .map(([key, value]) => ({
                    [key]: isRevenue ? (value === 0 ? 0 : value / 100) : value,
                })) // Convery cents to dollars (or handle if value is zero)
                .reduce((acc, curr) => ({...acc, ...curr}), {}); // Merge all objects into a single object

            return {id, ...values};
        });
    };

    formattedYoyData.revenue = formattedData(formattedYoyData.revenue, true);
    formattedYoyData.reservations = formattedData(
        formattedYoyData.reservations,
        false
    );

    return formattedYoyData;
};

/**
 * Gets the dashboard yoy accounting data for a collection of facilities
 * @function
 * @param {YoyRequest} data - The parameters for the report
 * @param {Object} config - The axios config
 * @return {Promise<DashboardAggregatedResponse>}
 * @throws {Error}
 *
 * @see '/control-panel-service/v1/yoy/graphs
 */
export const getYoyGraphs = async (data, config = {}) => {
    try {
        const response = await reportingPost(
            'yoy/graphs',
            formatRequestData(data),
            config
        );

        return formatGetYoyGraphs(response.data.data);
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Formats the request data for the dashboard yoy breakdown api endpoints
 * @function
 * @param {object} data - The data to format
 * @return {AccountingDashboardReportSummary[]}
 *
 * @memberof module:api/accounting/dto
 */
export const formatGetYoyBreakdown = (data) => {
    const formattedData = ObjectUtils.camelizeKeys(data);

    const updatedDated = formattedData.map((item) => ({
        ...item,
        currentRating: item.currentRating
            ? parseFloat(item.currentRating).toFixed(1)
            : null,
        revenue: {
            ...item.revenue,
            value: asDecimalCurrency(item.revenue.value / 100),
            previousValue: asDecimalCurrency(item.revenue.previousValue / 100),
            previousTotal: asDecimalCurrency(item.revenue.previousTotal / 100),
        },
        reservations: {
            ...item.reservations,
            value: asInteger(item.reservations.value),
            previousValue: asInteger(item.reservations.previousValue),
            previousTotal: asInteger(item.reservations.previousTotal),
        },
    }));

    return updatedDated;
};

/**
 * Gets an YOY breakdown for a given set of facilities
 * @function
 * @param {YoyRequest} data - The parameters for the report
 * @param {Object} config - The axios config
 * @return {Promise<AccountingReportYOY[]>}
 * @throws {Error}
 *
 * @see '/control-panel-service/v1/yoy/facility-breakdown
 */
export const getYoyBreakdown = async (data, config = {}) => {
    try {
        const response = await reportingPost(
            'yoy/facility-breakdown',
            formatRequestData(data),
            config
        );

        return formatGetYoyBreakdown(response.data.data);
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
